import React, {
  FunctionComponent,
  useState,
  useCallback,
  useEffect
} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import Snackbar, { SnackbarOrigin } from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import PropTypes from 'prop-types';
import { Box, CircularProgress } from '@mui/material';
import { clearSmartEntriesSelection } from 'modules/smartDiary/smartDiaryNotes/hooks';
import { SplashLogo } from 'modules/loading/SplashLogo';
import { recordMetric } from 'modules/metrics';
import { CopyrightFooter } from 'modules/common/components/CopyrightFooter';
import { useFeatureFlag } from 'modules/common/hooks';
import { popAlerts, LoginAlert } from './loginAlerts';
import LoginUi from './LoginUi';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    position: 'absolute',
    backgroundColor: theme.palette.common.white
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1
  },
  message: {
    [theme.breakpoints.up('sm')]: {
      ...theme.typography.h2
    },
    [theme.breakpoints.down('md')]: {
      ...theme.typography.h3
    },
    [theme.breakpoints.down('sm')]: {
      ...theme.typography.h4
    },
    position: 'relative',
    flexGrow: 0,
    flexShrink: 0,
    textAlign: 'center',
    margin: theme.spacing(2),
    padding: 0,
    boxSizing: 'border-box'
  },
  button: {
    ...theme.typography.h6,
    fontSize: '2.7vh',
    fontWeight: 600,
    [theme.breakpoints.up('xl')]: {
      padding: theme.spacing(1.5, 6, 1.5, 6)
    },
    [theme.breakpoints.down('xl')]: {
      padding: theme.spacing(1.25, 5, 1.25, 5)
    },
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(1, 4.5, 1, 4.5)
    }
  }
}));

const useLogoStyles = makeStyles(() => ({
  wrapper: {
    height: 'auto'
  }
}));

const snackBarOrigin: SnackbarOrigin = {
  horizontal: 'center',
  vertical: 'bottom'
};

const LoginPage: FunctionComponent<{
  loginRedirect: () => void;
  checkIsValidSession: () => Promise<boolean | undefined>;
}> = ({
  loginRedirect,
  checkIsValidSession
}: {
  loginRedirect: () => void;
  checkIsValidSession: () => Promise<boolean | undefined>;
}) => {
  const classes = useStyles();
  const logoClasses = useLogoStyles();

  const [disable, setDisable] = useState(false);
  const onLogin = useCallback(async () => {
    setDisable(true);
    await recordMetric('login-click', 2000);
    clearSmartEntriesSelection();
    sessionStorage.clear();
    loginRedirect();
  }, [loginRedirect]);

  const [alerts, setAlerts] = useState<LoginAlert[]>([]);

  const [isValidSession, setIsValidSession] = useState<boolean | undefined>(
    undefined
  );

  useEffect(() => {
    setAlerts(popAlerts());
  }, []);

  const dismissAlert = useCallback(
    (index) => () => {
      setAlerts(alerts.slice(index));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setAlerts]
  );

  useEffect(() => {
    (async function () {
      try {
        const isValidSession = await checkIsValidSession();
        setIsValidSession(isValidSession);
        const alerts = popAlerts();
        if (alerts.length) {
          setAlerts(alerts);
        }
      } catch (e) {
        setIsValidSession(false);
        console.error(e);
      }
    })();
  }, [checkIsValidSession]);

  const isTiwNewLoginDesign = useFeatureFlag('isTiwNewLoginDesign');
  useEffect(() => {
    sessionStorage.removeItem('ACTUALUSERURI');
    if (isValidSession) {
      onLogin();
    }
  }, [isValidSession, onLogin]);

  if (isValidSession === undefined) {
    return <SplashLogo />;
  }

  return (
    <div>
      {!isTiwNewLoginDesign ? (
        <>
          <Box className={classes.wrapper}>
            <div className={classes.contentWrapper}>
              {isTiwNewLoginDesign === undefined ? (
                <SplashLogo classes={logoClasses} />
              ) : (
                <>
                  <SplashLogo classes={logoClasses} />
                  <Box m={2}>
                    {disable ? (
                      <CircularProgress data-testid="login-progress" />
                    ) : (
                      <Button
                        className={classes.button}
                        data-testid="login-button"
                        variant="contained"
                        color="primary"
                        disabled={disable}
                        onClick={onLogin}
                      >
                        Login
                      </Button>
                    )}
                  </Box>
                </>
              )}
            </div>
          </Box>
          <CopyrightFooter />
        </>
      ) : (
        <LoginUi disable={disable} onClick={onLogin} />
      )}
      {alerts.map((alert, index) => (
        <Snackbar
          key={index}
          open
          style={{ marginBottom: `${32 + 64 * index}px` }}
          onClose={alert.dismissible ? dismissAlert(index) : undefined}
          anchorOrigin={snackBarOrigin}
        >
          <MuiAlert
            onClose={alert.dismissible ? dismissAlert(index) : undefined}
            severity={alert.severity}
          >
            {alert.message}
          </MuiAlert>
        </Snackbar>
      ))}
    </div>
  );
};

LoginPage.propTypes = {
  loginRedirect: PropTypes.func.isRequired
};

export default LoginPage;
